import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { LearningRecommendationsPage } from '@/learning-recommendations/pages/LearningRecommendationsPage'
import AIPlaybooks from '@/playbooks/pages/AIPlaybooks'
import PlaybookPage from '@/playbooks/pages/PlaybookPage'
import { SettingsModal } from '@/settings/components/SettingsModal'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FeatureFlagsProvider } from '@/shared/components/FeatureFlagsProvider'
import { GoogleAnalytics } from '@/shared/components/GoogleAnalytics'
import { HelpScoutChatBubble } from '@/shared/components/HelpScoutChatBubble'
import { PrivateRoute } from '@/shared/components/PrivateRoute'
import { Toaster as Sonner } from '@/shared/components/ui/sonner'
import { Toaster } from '@/shared/components/ui/toaster'
import { TooltipProvider } from '@/shared/components/ui/tooltip'
import { UpgradePlanModalProvider } from '@/shared/components/UpgradePlanModalProvider'
import { WorkspaceProvider } from '@/shared/components/WorkspaceProvider'
import { UserProvider } from '@/shared/contexts/userContext'
import Dashboard from '@/static-pages/Dashboard'
import Index from '@/static-pages/Index'
import NotFound from '@/static-pages/NotFound'
import Plans from '@/static-pages/Plans'
import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import {
  COOKIE_CONSENT_CONFIG,
  initializeDataLayerWithDefaultConsent,
} from './config/cookieConsent'
import { queryClient } from './config/queryClient'

const App = () => {
  useMemo(() => {
    // run before render
    initializeDataLayerWithDefaultConsent()
  }, [])

  useEffect(() => {
    CookieConsent.run(COOKIE_CONSENT_CONFIG)
  }, [])

  return (
    <FeatureFlagsProvider>
      <HelpScoutChatBubble />
      <GoogleAnalytics />
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <TooltipProvider>
                <WorkspaceProvider>
                  <UpgradePlanModalProvider>
                    <Toaster />
                    <Sonner />
                    <SettingsModal />

                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route
                        path="/dashboard"
                        element={
                          <PrivateRoute>
                            <Dashboard />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/playbooks"
                        element={
                          <PrivateRoute>
                            <AIPlaybooks />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/learning-recommendations"
                        element={
                          <PrivateRoute>
                            <LearningRecommendationsPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/playbooks/:id"
                        element={
                          <PrivateRoute>
                            <PlaybookPage />
                          </PrivateRoute>
                        }
                      />
                      <Route path="/plans" element={<Plans />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </UpgradePlanModalProvider>
                </WorkspaceProvider>
              </TooltipProvider>
            </ErrorBoundary>
          </BrowserRouter>
        </UserProvider>
      </QueryClientProvider>
    </FeatureFlagsProvider>
  )
}

export default App
